import { useState, useEffect } from "react";
import { RefreshCw, Wrench, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getPushDiag, forcePushRepair } from "@/lib/push";

const Row = ({ label, value, ok }) => (
  <div className="flex items-start justify-between gap-3 border-b border-border py-2.5 text-sm">
    <span className="text-muted-foreground shrink-0">{label}</span>
    <span className="font-mono-data text-xs text-right break-all flex items-center gap-1.5">
      {ok !== undefined && (ok ? <CheckCircle2 className="h-3.5 w-3.5 text-green-500 shrink-0" /> : <XCircle className="h-3.5 w-3.5 text-red-500 shrink-0" />)}
      {String(value)}
    </span>
  </div>
);

export default function PushDebug() {
  const [diag, setDiag] = useState(null);
  const [repairing, setRepairing] = useState(false);
  const [result, setResult] = useState(null);

  const refresh = () => getPushDiag().then(setDiag);
  useEffect(() => { refresh(); }, []);

  const repair = async () => {
    setRepairing(true);
    setResult(null);
    try {
      const r = await forcePushRepair();
      setResult({ ok: !!r.token, msg: r.token ? "Abonnement réparé — token obtenu ✅" : "Toujours pas de token après réparation ❌ (id: " + r.id + ")" });
    } catch (e) {
      setResult({ ok: false, msg: "Erreur : " + (e?.message || e) });
    } finally {
      setRepairing(false);
      refresh();
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground px-4 py-10">
      <div className="max-w-lg mx-auto">
        <h1 className="font-heading font-bold text-xl mb-1">Diagnostic notifications push</h1>
        <p className="text-xs text-muted-foreground mb-6">Page technique — partagez une capture d'écran de ce diagnostic.</p>
        {!diag ? (
          <p className="text-sm text-muted-foreground">Analyse en cours…</p>
        ) : (
          <div className="border border-border rounded-xl p-4 bg-card" data-testid="push-diag">
            <Row label="Navigateur compatible" value={diag.supported ? "oui" : "non"} ok={diag.supported} />
            <Row label="Permission" value={diag.permission} ok={diag.permission === "granted"} />
            <Row label="Origine" value={diag.origin} />
            <Row label="SDK OneSignal initialisé" value={diag.initOk ? "oui" : "non"} ok={diag.initOk} />
            {diag.initError && <Row label="Erreur init SDK" value={diag.initError} ok={false} />}
            <Row label="ID abonnement OneSignal" value={diag.subscriptionId || "aucun"} ok={!!diag.subscriptionId} />
            <Row label="Token push" value={diag.token || "AUCUN"} ok={!!diag.token} />
            <Row label="Opt-in" value={diag.optedIn ? "oui" : "non"} ok={diag.optedIn} />
            <Row label="Compte lié (external id)" value={diag.externalId || "non connecté"} />
            {diag.sdkError && <Row label="Erreur SDK" value={diag.sdkError} ok={false} />}
            <div className="pt-3 text-xs text-muted-foreground">
              <p className="font-medium mb-1">Service workers :</p>
              {(diag.workers || []).map((w, i) => <p key={i} className="font-mono-data break-all">• {w}</p>)}
              {(!diag.workers || diag.workers.length === 0) && <p>aucun</p>}
            </div>
          </div>
        )}
        {result && (
          <div className={`mt-4 text-sm px-4 py-3 rounded-lg ${result.ok ? "bg-green-500/10 text-green-500" : "bg-red-500/10 text-red-500"}`} data-testid="repair-result">
            {result.msg}
          </div>
        )}
        <div className="flex gap-2 mt-5">
          <Button onClick={repair} disabled={repairing} data-testid="repair-btn" className="rounded-full flex-1">
            <Wrench className="h-4 w-4 mr-2" /> {repairing ? "Réparation…" : "Réparer l'abonnement"}
          </Button>
          <Button variant="outline" onClick={refresh} data-testid="refresh-diag-btn" className="rounded-full">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
